import React, { useContext, useEffect, useState } from 'react'
import FormMessageBanner from '../../../../components/common/FormMessageBanner'
import Loader from '../../../../components/common/Loader'
import { ReactComponent as AlertIcon } from '../../../../assets/icons/alert-circle.svg'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { ContentWrapper, DetailsLabel, DetailsValue, EditButton, SectionTitle, DetailsBox, WalletAddress } from './styles'
import { DetailsContent, DetailsRow, DetailsSectionHeader, ItemWrapper } from '../styles'
import { useNavigate, useParams } from 'react-router-dom'
import { formatCurrencyAmount } from '../../../../utils/currency'
import { Button } from '../../../../components/common/Button'
import EventFactory from "../../../../utils/eventFactory";
import { sendOTP } from '../../../../api/usersAPI'
import WalletOTP from '../../components/SendFunds/components/WalletOTP'
import { clearUserData, setStep } from "../../redux/formDataSlice";
import { SuccessAlertContext } from "../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import CustomModal from '../../../../components/common/CustomModal'
import { SuccessContent, SuccessContentWrapper, SuccessHeader, SuccessModalWrapper } from '../../AllWallets/style'
import { ReactComponent as SuccessIcon } from "../../../../assets/icons/success-icon.svg";
import { useStableCoinPayout } from '../../../../app/queries/stableCoins'
import { v4 as uuidv4 } from "uuid";
import { merchantBusinessKeys } from '../../../Settings/settingsSlice'
import dayjs from 'dayjs'
import { capitalizeCurrencyCode } from '../../../../utils/helpers'
import { allWallets } from '../../redux/slice'


type ReviewTypes = {
  handlePrevious: () => void;
};

type ReviewState = {
  publicKey: string;
  taxRefId: string;
  loading: boolean;
  verifyOTP: boolean;
  stableCoinSuccessModal: boolean;
};

const Review = ({ handlePrevious }: ReviewTypes) => {

  const { currencyCode } = useParams<{ currencyCode: string }>();
  const capitalizedCurrencyCode = currencyCode?.toUpperCase();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const eventFactory = EventFactory.getInstance()

  const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
  const { loading } = useAppSelector((state) => state?.wallets || {});
  const {
    userObj: { accountUser }, businessId
  } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);

  const successAlertContext = useContext(SuccessAlertContext);

  const { onShowAlert: onSuccess } = successAlertContext || {};
  const { onShowAlert } = useContext(NetworkErrorAlertContext);

  const [reviewData, setReviewData] = useState<ReviewState>({
    publicKey: keys?.data?.publicKey ?? "",
    taxRefId: "",
    loading: false,
    verifyOTP: false,
    stableCoinSuccessModal: false,
  });

  const { mutateAsync: sendStableCoinPayout } = useStableCoinPayout()

  const updateReviewData = (fields: Partial<ReviewState>) =>
    setReviewData((prev) => ({ ...prev, ...fields }));

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    updateReviewData({ taxRefId: `stc-${uuidv4()}` });
  }, []);

  const handleSendOTP = async (user: { email: string }) => {
    updateReviewData({ loading: true });
    try {
      await sendOTP(user);

      eventFactory.captureEvent("payout_otp", {
        status: "success",
        currency: capitalizeCurrencyCode(currencyCode),
      });

      updateReviewData({ verifyOTP: true, loading: false });
    } catch (err: any) {

      updateReviewData({ loading: false });
      let message = "";
      const errorObj = err?.response?.data;

      if (typeof errorObj?.error === "string") message = errorObj.error;
      else if (typeof errorObj?.message === "string")
        message = errorObj.message;
      else if (typeof errorObj === "string") message = errorObj;
      else message = "Failed to send OTP";

      eventFactory.captureEvent("payout_otp", {
        status: "failure",
        failure_reason: message,
        currency: capitalizeCurrencyCode(currencyCode) || "",
      });
    }
  };

  const handlePayout = async () => {

    const stableCoinPayoutPayload = {
      data: {
        amount: getSavedData?.amount,
        network: getSavedData?.network,
        walletDestinationAddress: getSavedData?.walletAddress,
        currency: capitalizedCurrencyCode,
        requestId: reviewData.taxRefId,
        narration: "",
      },
      token: reviewData.publicKey,
    }

    try {
      await sendStableCoinPayout(stableCoinPayoutPayload).then((result) => {
        if (result) {
          onSuccess("Payout successfully sent");
          updateReviewData({ stableCoinSuccessModal: true });
          dispatch(allWallets({ businessId }))
          setTimeout(() => {
            dispatch(clearUserData());
            navigate(`/wallets/${currencyCode}`);
          }, 2000)
        }
      })
    } catch (error) {
      onShowAlert("", error?.data?.message);
    } finally {
      updateReviewData({ loading: false });
    }
  };

  return (
    <>
      <Loader isLoading={loading} />

      <ContentWrapper>
        <FormMessageBanner
          title="Verify your address and network"
          body="Klasha is not liable for incorrect address or network errors. Always double-check before making payment."
          icon={<AlertIcon />}
        />
        <DetailsBox>
          <DetailsSectionHeader>
            <SectionTitle>Transaction details</SectionTitle>
            <EditButton onClick={() => dispatch(setStep(0))}>
              <span>Edit</span>
            </EditButton>
          </DetailsSectionHeader>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Network</DetailsLabel>
              <DetailsValue>
                {getSavedData?.network}
              </DetailsValue>
            </DetailsRow>
          </DetailsContent>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Recipient's Wallet Address</DetailsLabel>
              <WalletAddress>
                {getSavedData?.walletAddress}
              </WalletAddress>
            </DetailsRow>
          </DetailsContent>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Recipient's Amount</DetailsLabel>
              <DetailsValue>
                {formatCurrencyAmount(capitalizedCurrencyCode ?? "USDC", getSavedData?.amount ?? 0)}
              </DetailsValue>
            </DetailsRow>
          </DetailsContent>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Processing Fee</DetailsLabel>
              <DetailsValue>
                {formatCurrencyAmount(capitalizedCurrencyCode ?? "USDC", getSavedData?.processingFee ?? 0)}
              </DetailsValue>
            </DetailsRow>
          </DetailsContent>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Total Amount</DetailsLabel>
              <DetailsValue>
                {formatCurrencyAmount(capitalizedCurrencyCode ?? "USDC", getSavedData?.totalAmount ?? 0)}
              </DetailsValue>
            </DetailsRow>
          </DetailsContent>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Date</DetailsLabel>
              <DetailsValue>
                {dayjs(Date.now()).format("DD/MM/YYYY")}
              </DetailsValue>
            </DetailsRow>
          </DetailsContent>
          <DetailsContent>
            <DetailsRow>
              <DetailsLabel>Narration</DetailsLabel>
              <DetailsValue>
                {getSavedData?.paymentDescription}
              </DetailsValue>
            </DetailsRow>
          </DetailsContent>
        </DetailsBox>

        <ItemWrapper>
          <Button
            onClick={handlePrevious}
            label="Prev"
            height="48px"
            theme="alternate"
            width="203px"
          />

          <Button
            label="Continue"
            height="48px"
            onClick={() => handleSendOTP(accountUser?.email)}
          />
        </ItemWrapper>
      </ContentWrapper>

      {reviewData.verifyOTP && (
        <WalletOTP
          setVerifyOTP={(v) => updateReviewData({ verifyOTP: v })}
          makePayment={handlePayout}
        />
      )}

      {reviewData.stableCoinSuccessModal && (
        <CustomModal
          isModalVisible={reviewData.stableCoinSuccessModal}
          onClose={() => updateReviewData({ stableCoinSuccessModal: false })}
          width="480px"
        >
          <SuccessModalWrapper>
            <SuccessIcon />
            <SuccessContentWrapper>
              <SuccessHeader>
                Funds sent successfully!
              </SuccessHeader>
              <SuccessContent>
                Redirecting you to your wallet dashboard
              </SuccessContent>
            </SuccessContentWrapper>
          </SuccessModalWrapper>

        </CustomModal>
      )}
    </>
  )
}

export default Review